import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router";

import { USER_ROLE_ADMIN } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { AddCircleOutline, EastOutlined, SearchOutlined } from "@mui/icons-material";
import { Box, Button, Grid, IconButton, InputAdornment, Stack, Tooltip, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { MemberManagementIcon } from "components/Icons/Icons";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { formatProducerName, formatProducerNameSearchFields } from "utility/formatters";

import { BrokerIcon } from "../Icons/Icons";
import AgentAutoComplete, { dedupeBrokersByToken, normalizeAgentTokens } from "./AgentAutoComplete";

export default function ClientWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const navigate = useNavigate();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const { enqueueSnackbar } = useSnackbar();

  // ---

  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);

  const [userGrants, setUserGrants] = useState([]);

  const [brokers, setBrokers] = useState(null);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false);

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    filter_agent_token: normalizeAgentTokens(
      globalState?.filter_agent_token?.length
        ? globalState.filter_agent_token
        : [globalState?.agent_token].filter(Boolean)
    ),
    search_producer_name: globalState?.search_text_portfolio,
  });
  const selectedBroker = useMemo(() => {
    const tokens = new Set(normalizeAgentTokens(filter?.filter_agent_token));
    return dedupeBrokersByToken(
      brokers?.filter((broker) => tokens.has(String(broker?.agent_token ?? "").toLowerCase())) || []
    );
  }, [brokers, filter?.filter_agent_token, globalState?.agent_token]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: filter?.filter_agent_token, //array
      },
    });
  }, [filter?.filter_agent_token]);

  const filterHandleChangeState = (event, value) => {
    if (value == null && event.target.value != null) {
      value = event.target.value;
    }

    dispatchFilter({
      type: "update",
      payload: { key: event.target.name, value: value },
    });

    // Persist search text?
    if (event.target.name == "search_producer_name") {
      persistSearchText(event?.target?.value || "");
    }
  };

  const persistSearchText = async (searchText) => {
    globalDispatch({
      type: "update",
      payload: {
        key: "search_text_portfolio",
        value: searchText,
      },
    });
  };

  const _loadClients = async () => {
    try {
      setIsLoadingCurrent(true);
      const _userGrantsDataFilter = {
        agent_token: globalState?.agent_token,
        is_active: true,
      };

      if (filter.filter_agent_token?.length) {
        const promises = filter.filter_agent_token.map(async (agentToken) => {
          if (filter?.filter_agent_token && filter?.filter_agent_token.length) {
            _userGrantsDataFilter.filter_agent_token = agentToken;
          }

          const apiClaims = {};
          if (globalState?.user_role != USER_ROLE_ADMIN) {
            apiClaims.agent_token = globalState?.agent_token;
          }

          return await UsersApiHelper.users_search("Client", _userGrantsDataFilter, apiClaims);
        });

        const allResults = await Promise.all(promises);
        // Get distinct, from combined results.
        const distinctAllResults = _.uniqBy(allResults.flat(), "producer_token");

        setUserGrants(distinctAllResults);
      } else if (globalState?.agent_token && filter?.search_producer_name?.length) {
        const apiClaims = { agent_token: globalState?.agent_token };
        const results = await UsersApiHelper.users_search("Client", _userGrantsDataFilter, apiClaims);
        setUserGrants(results);
      } else {
        setUserGrants([]);
      }

      setIsLoadingCurrent(false);
    } catch (error) {
      setIsLoadingCurrent(false);
      handleError(error);
    }
  };

  useEffect(() => {
    _loadClients();
  }, [filter]);

  const loadBrokers = async () => {
    try {
      if (globalState?.agent_token) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {
          agent_token: globalState?.agent_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      } else if (globalState?.user_role == USER_ROLE_ADMIN) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {};
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);

        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      }
    } catch (err) {
      setIsLoadingBrokers(false);
      setBrokers(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.agent_token || globalState?.user_role == USER_ROLE_ADMIN) {
      setIsLoadingBrokers(true);
      loadBrokers();
    }
  }, [globalState?.agent_token, globalState?.user_role, filter]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  // ---

  const setViewAccountProducer = async (currentProducer) => {
    if (currentProducer?.producer_token) {
      globalDispatch({
        type: "setProducer",
        payload: {
          producer_token: currentProducer?.producer_token,
          agent_token: globalState?.agent_token,
        },
      });

      if (globalState.is_broker) {
        const _selectedYear = globalState?.crop_year || getCurrentReinsuranceYear();
        navigate(`/marketing/position_summary/${currentProducer?.producer_token}/${_selectedYear}`);
      } else {
        navigate("/vault");
      }
    }
  };

  // ---

  const _formatProducerNameCallback = useCallback((row) => {
    return formatProducerName(row);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "producer_token",
        headerName: "Client",
        minWidth: 200,
        flex: 3,
        valueGetter: (value, row) => {
          return _formatProducerNameCallback(row);
        },
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 200,
        flex: 2,
      },
      {
        field: "manage",
        type: "actions",
        headerName: "Settings",
        sortable: false,
        getActions: (params) => {
          const globalProducerToken = globalState?.producer_token;
          const rowProducerToken = params.row?.producer_token;

          const isImpersonating = Boolean(globalProducerToken);
          // True if the global token matches the row's token.
          const tokensMatch = globalProducerToken === rowProducerToken;

          const isDisabled = isImpersonating && !tokensMatch;

          // Highlight only when impersonating this specific producer.
          const shouldHighlight = isImpersonating && tokensMatch;

          return [
            <GridActionsCellItem
              key="manage"
              icon={<MemberManagementIcon color={shouldHighlight ? "secondary.contrastText" : "inherit"} />}
              label="Manage"
              data-testid={`manage-client-button-${params.row?.id}`}
              onClick={() => {
                if (params.row?.id) {
                  navigate(`/broker/clients/${params.row.id}`);
                } else {
                  console.error("Manage action: Row ID is missing, cannot navigate.", params.row);
                }
              }}
              disabled={isDisabled}
              sx={{
                backgroundColor: shouldHighlight ? "primary.main" : "transparent",
                color: shouldHighlight ? "secondary.contrastText" : "inherit",
                "&:hover": {
                  backgroundColor: shouldHighlight ? "primary.dark" : isDisabled ? "transparent" : "",
                },
              }}
            />,
          ];
        },
        flex: 1,
      },
      {
        field: "view",
        type: "actions",
        headerName: "View Account",
        sortable: false,
        getActions: (params) => {
          const globalProducerToken = globalState?.producer_token;
          const rowProducerToken = params.row?.producer_token;

          // If a global producer token is set (i.e., "viewing as" or impersonating),
          // only show the "View Account" action for that specific producer.
          if (globalProducerToken && globalProducerToken !== rowProducerToken) {
            return [];
          }

          // If no global producer token is set (i.e., not "viewing as" anyone),
          // or if it is set and matches the row,
          // then show the "View Account" action, but only if the row itself has a producer token.
          if (!rowProducerToken) {
            return [];
          }

          return [
            <GridActionsCellItem
              key="view"
              icon={<EastOutlined />}
              label="View Account"
              onClick={() => {
                if (params.row) {
                  setViewAccountProducer(params.row);
                } else {
                  console.error("View Account action: Row data is missing.");
                }
              }}
            />,
          ];
        },
        flex: 1,
      },
    ],
    [globalState?.producer_token, globalState?.agent_token, navigate]
  );

  const filtereduserGrants = useMemo(
    () =>
      userGrants?.filter((row) => {
        if (filter?.search_producer_name) {
          return formatProducerNameSearchFields(row)
            ?.toLowerCase()
            .includes(filter?.search_producer_name.toLowerCase());
        }
        return true;
      }),
    [userGrants, filter?.search_producer_name]
  );

  const handleChangeAgentToken = async (agentTokens) => {
    dispatchFilter({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: normalizeAgentTokens(agentTokens.map((item) => item.agent_token)),
      },
    });
  };

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Clients"
        category={{
          title: "Broker",
          icon: <BrokerIcon />,
        }}
      />

      <Grid container>
        <Grid item xs={12}>
          <Stack
            className="headerWithMenuButton"
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: "start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: "480px" },
                "& .MuiFormControl-root": {
                  width: "100% !important",
                },
              }}
            >
              <AgentAutoComplete brokers={brokers} selectedBroker={selectedBroker} onChange={handleChangeAgentToken} />
            </Box>

            <StatefulControlledTextField
              label="Search"
              name="search_producer_name"
              className="search-input"
              isRequired={false}
              defaultValue={filter?.search_producer_name || ""}
              onChange={filterHandleChangeState}
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip title="Search">
                    <IconButton edge="end" color="primary">
                      <SearchOutlined />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            />
          </Stack>
        </Grid>
      </Grid>
      <TilleyDataGrid
        rows={filtereduserGrants || []}
        columns={columns}
        pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="client-workspace-grid"
        getRowId={(row) => row.id}
        isLoading={isLoadingCurrent}
      />
      <WorkspaceActionBar
        content={
          <>
            <Box>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                onClick={() => {
                  navigate(`/broker/clients/0`);
                }}
              >
                <AddCircleOutline sx={{ mr: 1 }} />
                New Client
              </Button>
            </Box>
          </>
        }
      />
    </>
  );
}
